import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, AlertCircle } from "lucide-react";
import { GoodacresHeader } from "@/components/GoodacresLogo";
import { API } from "@/App";

export const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [autoLoggingIn, setAutoLoggingIn] = useState(true);
  const [error, setError] = useState("");
  const [sessionExpired, setSessionExpired] = useState(false);

  // Check for session expired flag
  useEffect(() => {
    if (searchParams.get('session') === 'expired') {
      setSessionExpired(true);
      toast.error("Your session has expired. Please log in again.");
    }
  }, [searchParams]);

  // Check for remember token on mount
  useEffect(() => {
    const checkRememberToken = async () => {
      const rememberToken = localStorage.getItem("remember_token");
      if (rememberToken) {
        try {
          const response = await axios.post(`${API}/auth/login/remember`, { remember_token: rememberToken });
          const { access_token, user } = response.data;
          
          localStorage.setItem("token", access_token);
          localStorage.setItem("user", JSON.stringify(user));
          
          toast.success(`Welcome back, ${user.name}!`);
          redirectUser(user);
          return;
        } catch (err) {
          // Token invalid/expired, clear it
          localStorage.removeItem("remember_token");
          console.log("Remember token expired, showing login form");
        }
      }
      setAutoLoggingIn(false);
    };
    
    checkRememberToken();
  }, []);

  const redirectUser = (user) => {
    // Sales Negotiators (Tracey and team) go to clean progression dashboard
    if (user.role === "sales_negotiator") {
      window.location.href = "/progression";
      return;
    }
    
    // Redirect based on role
    const routes = {
      tenant: "/tenant",
      contractor: "/contractor",
      landlord: "/landlord",
      admin: "/home",
      director: "/home",
      sales_agent: "/home",
      accounts: "/accounts",
      inventory_clerk: "/inventory",
      lettings_manager: "/home",
      lettings_negotiator: "/home",
      seller_solicitor: "/solicitor",
      buyer_solicitor: "/solicitor",
      broker: "/sales",
      seller: "/portal/seller",
      buyer: "/portal/buyer"
    };
    navigate(routes[user.role] || "/home");
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API}/auth/login`, { 
        email: email.trim().toLowerCase(), 
        password,
        remember_me: rememberMe
      });
      const { access_token, remember_token, user } = response.data;
      
      // Store tokens
      localStorage.setItem("token", access_token);
      localStorage.setItem("user", JSON.stringify(user));
      
      // Store remember token if provided
      if (remember_token) {
        localStorage.setItem("remember_token", remember_token);
      }
      
      toast.success(`Welcome back, ${user.name}!`);
      redirectUser(user);
    } catch (error) {
      const detail = error.response?.data?.detail || "Login failed. Please try again.";
      setError(detail);
      
      // Show appropriate toast based on error type
      if (error.response?.status === 423) {
        toast.error("Account locked", { description: detail });
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Show loading while checking remember token
  if (autoLoggingIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600 mx-auto mb-4" />
          <p className="text-slate-600">Checking your session...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo - Using the full Goodacres logo */}
        <div className="flex justify-center mb-8">
          <img 
            src="/goodacres-full-logo.jpg" 
            alt="Goodacres" 
            className="h-48 w-auto"
          />
        </div>

        <Card className="border-0 shadow-lg">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl">Welcome back</CardTitle>
            <CardDescription>Sign in to your account</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Session Expired Warning */}
            {sessionExpired && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
                <p className="text-sm text-amber-800">Your session has expired. Please sign in again.</p>
              </div>
            )}
            
            {/* Google Sign In */}
            <Button
              data-testid="google-login-btn"
              variant="outline"
              className="w-full h-12 rounded-xl"
              onClick={handleGoogleLogin}
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-muted-foreground">Or continue with email</span>
              </div>
            </div>

            {/* Error Alert */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Email/Password Form */}
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  data-testid="email-input"
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 rounded-lg"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  data-testid="password-input"
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 rounded-lg"
                  required
                />
              </div>
              
              {/* Remember Me Checkbox */}
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="remember-me" 
                  checked={rememberMe}
                  onCheckedChange={setRememberMe}
                  data-testid="remember-me-checkbox"
                />
                <label 
                  htmlFor="remember-me" 
                  className="text-sm font-medium text-slate-600 cursor-pointer select-none"
                >
                  Remember me for 30 days
                </label>
              </div>
              
              <Button
                data-testid="login-submit-btn"
                type="submit"
                className="w-full h-12 rounded-xl text-base"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  "Sign in"
                )}
              </Button>
            </form>

            <p className="text-center text-sm text-muted-foreground">
              Don&apos;t have an account?{" "}
              <Link to="/register" className="text-primary hover:underline font-medium">
                Sign up
              </Link>
            </p>
            
            <div className="mt-4 pt-4 border-t text-center">
              <p className="text-sm text-muted-foreground mb-2">Are you a client?</p>
              <Link 
                to="/portal/login" 
                className="text-emerald-600 hover:underline font-medium text-sm"
                data-testid="portal-login-link"
              >
                Go to Client Portal →
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
